import { router } from "@inertiajs/react"
import {
  CheckCircle2,
  Clock,
  Eye,
  Gift,
  type LucideIcon,
  MapPin,
  Package,
  PackageCheck,
  RotateCcw,
  Send,
  ShoppingBag,
  Truck,
  Upload,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { MOTION_BEATS } from "@/lib/motion"
import { cn } from "@/lib/utils"
import type { CollabCta, CollabCtaKind } from "@/types/collab"

/**
 * OKS-562 §08 — the actionable Accept / Mark-posted (submit) CTAs play the
 * one-shot `mission-accept` confirm nudge (scale .96→1.04→1, 200ms ease-out)
 * as they mount. Bounded to these two forward-action kinds; disabled/progress
 * affordances stay static. `motion-safe:` gated (no nudge under reduced-motion).
 */
const NUDGE_KINDS: readonly CollabCtaKind[] = ["accept", "submit"]

/**
 * CTAButton — the single interactive region on a CollabCard.
 *
 * Per K6 (UX Spec), card CTAs are `<button>` (not `<a>` / `<Link>`) with an
 * explicit `aria-label` that includes the mission title. A single CTA per card
 * — never two. `router.visit(cta.href)` honours cycle-3's deferred destinations:
 * backend emits `"#"` stubs in V1, and we pass them through verbatim so the
 * router no-ops gracefully until #29–#32 populate the targets.
 *
 * OKS-445 — the `submit` picker-entry kind is the one CTA that neither
 * navigates nor POSTs: it invokes `onSubmitTap` to open the OKS-35 TikTok
 * post picker Dialog client-side.
 *
 * Variant mapping is pure — the K6 table lives in one place.
 */

/** K6 mapping — shape: icon + Button variant + a11y + label key. */
interface CtaSpec {
  icon: LucideIcon
  variant: "default" | "outline"
  labelFallbackKey: string
  ariaKey: string
}

/**
 * Label-key → aria-key overrides.
 *
 * By default the aria-label is resolved from `CTA_SPECS[cta.kind].ariaKey`,
 * which is correct when visible label and announcement share the same word
 * ("Submit" → "Submit content for <title>"). When backend swaps the visible
 * label (K5 resubmission flips `cta.label_key` to `creator_home.cta.submit_again`),
 * the announcement must follow, otherwise a screen reader user hears
 * "Submit content for X" while sighted users see "Submit again". The dictionary
 * already ships the paired aria entry under `creator_home.cta.aria.submit_again`.
 */
const LABEL_KEY_ARIA_OVERRIDES: Readonly<Record<string, string>> = {
  "creator_home.cta.submit_again": "creator_home.cta.aria.submit_again",
}

function ariaKeyFor(
  labelKey: string | undefined,
  fallbackAriaKey: string,
): string {
  if (labelKey && labelKey in LABEL_KEY_ARIA_OVERRIDES) {
    return LABEL_KEY_ARIA_OVERRIDES[labelKey]
  }
  return fallbackAriaKey
}

const CTA_SPECS: Record<CollabCtaKind, CtaSpec> = {
  // OKS-32 — `apply` flips from a stub (Hand icon, no-op) to a real
  // POST CTA (Send icon, dispatches `event=apply` to creator
  // transitions endpoint) when the backend emits it on a Drop
  // `assigned` row. The icon swap matches UX Spec §2.1; the
  // disambiguation between stub (legacy OKS-7 / Promotional) and real
  // (Drop) is by `cta.method === "post"` at the click handler.
  apply: {
    icon: Send,
    variant: "default",
    labelFallbackKey: "creator_home.cta.apply",
    ariaKey: "creator_home.cta.aria.apply",
  },
  // OKS-988 — the drop ENTRY kinds. `request_product` is the ordinary case;
  // `participate_owned` is PRIMARY when the creator already has the product
  // (the cheapest correct path); `take_part` is the neutral wording for a
  // productless drop or an unknown mission type.
  request_product: {
    icon: Package,
    variant: "default",
    labelFallbackKey: "creator_home.cta.request_product",
    ariaKey: "creator_home.cta.aria.request_product",
  },
  participate_owned: {
    icon: CheckCircle2,
    variant: "default",
    labelFallbackKey: "creator_home.cta.participate_owned",
    ariaKey: "creator_home.cta.aria.participate_owned",
  },
  take_part: {
    icon: Send,
    variant: "default",
    labelFallbackKey: "creator_home.cta.take_part",
    ariaKey: "creator_home.cta.aria.take_part",
  },
  // ⚠️ DISABLED, and its LABEL names the cause ("Plus de produits
  // disponibles" / "Toutes les places sont prises"). The backend picks which
  // by setting `label_key`, so the accessible name always states why the
  // control is dead — a screen-reader user must never meet a nameless
  // disabled button (UX §6). Listed in DISABLED_KINDS below.
  capacity_refused: {
    icon: Package,
    variant: "outline",
    labelFallbackKey: "creator_home.cta.capacity_no_slots",
    ariaKey: "creator_home.cta.capacity_no_slots",
  },
  accept: {
    icon: CheckCircle2,
    variant: "default",
    labelFallbackKey: "creator_home.cta.accept",
    ariaKey: "creator_home.cta.aria.accept",
  },
  buy_with_code: {
    icon: ShoppingBag,
    variant: "default",
    labelFallbackKey: "creator_home.cta.buy_with_code",
    ariaKey: "creator_home.cta.aria.buy_with_code",
  },
  // OKS-445 — the submission-ready picker entry. Backend (`CtaResolver`)
  // emits this on Open `accepted` / Drop+Promotional `delivered` with
  // `label_key: "submissions.creator.pick_cta"` and a nil href/method/event:
  // it is NOT a POST. Tapping it calls `onSubmitTap` to open the OKS-35
  // TikTok post picker Dialog client-side. The aria key
  // (`creator_home.cta.aria.submit`, title-scoped) announces the posting
  // action — never the old "Mark posted" verb (UX Spec §6).
  submit: {
    icon: Upload,
    variant: "default",
    labelFallbackKey: "creator_home.cta.submit",
    ariaKey: "creator_home.cta.aria.submit",
  },
  view_status: {
    icon: Eye,
    // K6 — only "In review" uses secondary outline; every other kind is primary.
    variant: "outline",
    labelFallbackKey: "creator_home.cta.view_status",
    ariaKey: "creator_home.cta.aria.view_status",
  },
  // OKS-34 — disabled affordance shown after a submission posts, while the
  // brand verifies. No href, no event — handler is a no-op.
  awaiting_review: {
    icon: Clock,
    variant: "outline",
    labelFallbackKey: "collabs.mark_posted.awaiting",
    ariaKey: "collabs.mark_posted.awaiting",
  },
  // OKS-32 — Drop creator-applied state (post-Apply). Disabled
  // affordance: "Brand will review your application". Decline overflow
  // remains accessible via the card menu.
  awaiting_brand: {
    icon: Clock,
    variant: "outline",
    labelFallbackKey: "collabs.applied.awaiting",
    ariaKey: "collabs.applied.awaiting",
  },
  // OKS-32 — Drop `to_ship` state. Read-only progress affordance:
  // "Brand is preparing your box". No CTA buttons — UX Spec §2.1.
  to_ship_progress: {
    icon: Package,
    variant: "outline",
    labelFallbackKey: "collabs.to_ship.progress",
    ariaKey: "collabs.to_ship.progress",
  },
  // OKS-1001 — Drop/Promotional `to_ship` with the address unconfirmed and no
  // phone captured. Disabled, exactly like `to_ship_progress` (OKS-991 still
  // withholds the confirm action here). `MapPin` rather than `Package`: the
  // question on screen is the ADDRESS, and it is the same glyph the
  // `confirm_address` CTA carries, so the creator sees one wayfinding mark
  // across the withheld state and the action that follows it.
  to_ship_address_pending: {
    icon: MapPin,
    variant: "outline",
    labelFallbackKey: "collabs.to_ship.awaiting_address",
    ariaKey: "collabs.to_ship.awaiting_address",
  },
  // OKS-1040 — the product choice is outstanding (precedes the address kind).
  to_ship_product_pending: {
    icon: Gift,
    variant: "outline",
    labelFallbackKey: "collabs.to_ship.awaiting_product_choice",
    ariaKey: "collabs.to_ship.awaiting_product_choice",
  },
  // OKS-32 — Drop `shipped` state. Read-only progress affordance:
  // "On the way". The tracking number, when present, renders BELOW
  // this button via `TrackingNumberLine` in `collab-card.tsx`.
  shipped_progress: {
    icon: Truck,
    variant: "outline",
    labelFallbackKey: "collabs.shipped.progress",
    ariaKey: "collabs.shipped.progress",
  },
  // OKS-33 — Promotional `accepted` state. The PromotionalMissionCard
  // renders the actionable affordance (promo code copy + buy CTA) in
  // its own body; the generic CTAButton row is a read-only fallback
  // shown only when the legacy CollabCard path consumes a Promotional
  // row (shouldn't happen post-OKS-33, but kept for type exhaustion).
  accepted_progress: {
    icon: Clock,
    variant: "outline",
    labelFallbackKey: "collabs.accepted.promotional_helper",
    ariaKey: "collabs.accepted.promotional_helper",
  },
  // OKS-33 — Promotional `purchased` state. Read-only progress
  // affordance: "Your order has been spotted! Brand is preparing your
  // shipment."
  purchased_progress: {
    icon: ShoppingBag,
    variant: "outline",
    labelFallbackKey: "collabs.purchased.progress",
    ariaKey: "collabs.purchased.progress",
  },
  // OKS-482 — gifting FLAG CTAs (real POST, primary). `confirm_address` is
  // emitted on a `to_ship` drop/promotional collab before the address is
  // confirmed; `confirm_receipt` on a `shipped` OR `delivered` one before
  // receipt (OKS-992 added the `shipped` half — the creator who opens the box
  // before the brand marks it). Both dispatch via the `cta.method === "post"`
  // branch in `handleClick`.
  //
  // ⚠️ No new entry was needed for OKS-992: ONE `confirm_receipt` kind serves
  // both states, which is the point — the creator meets one control and one
  // label, and the server decides which write runs.
  confirm_address: {
    icon: MapPin,
    variant: "default",
    labelFallbackKey: "creator_home.cta.confirm_address",
    ariaKey: "creator_home.cta.aria.confirm_address",
  },
  confirm_receipt: {
    icon: PackageCheck,
    variant: "default",
    labelFallbackKey: "creator_home.cta.confirm_receipt",
    ariaKey: "creator_home.cta.aria.confirm_receipt",
  },
  // OKS-483 — `request_reopen` is a real POST CTA on a terminal `expired`
  // collab (loss-aversion "Request reopen"). Secondary/outline per UX A2;
  // dispatches `event=request_reopen` via the `cta.method === "post"`
  // branch in `handleClick`.
  request_reopen: {
    icon: RotateCcw,
    variant: "outline",
    labelFallbackKey: "creator_home.cta.request_reopen",
    ariaKey: "creator_home.cta.aria.request_reopen",
  },
  // OKS-483 — `reopen_requested` is the disabled interim affordance shown
  // while the brand decides ("Waiting on {brand}"). No href/event — handler
  // is a no-op (DISABLED_KINDS below).
  reopen_requested: {
    icon: Clock,
    variant: "outline",
    labelFallbackKey: "collabs.reopen_requested.waiting",
    ariaKey: "collabs.reopen_requested.waiting",
  },
}

interface CTAButtonProps {
  cta: CollabCta
  /**
   * The mission title — injected into the `aria-label` so a screen reader
   * never hears a bare verb like "Submit".
   */
  missionTitle: string
  /**
   * Offline override — when true, tapping the button does nothing (caller
   * wires the Sonner toast). Keeps the button focusable + readable; only
   * the click handler is suppressed. Per K5 offline behaviour.
   */
  offline?: boolean
  /** Optional handler called when offline (consumer fires the toast). */
  onOfflineTap?: () => void
  /**
   * OKS-445 — handler for the `submit` picker-entry CTA. Tapping the
   * submission-ready CTA opens the OKS-35 TikTok post picker (the picker
   * Dialog is owned by the sibling `SubmissionsPanel`; the parent list item
   * lifts the open state and passes this callback down). No-op when absent.
   */
  onSubmitTap?: () => void
  /**
   * OKS-990 — when present, a tap runs this INSTEAD of the transition POST.
   * The drop picker uses it so the creator chooses a product before the grant.
   * Absent on every other surface, which is what keeps the single-product tap
   * a single `router.post` (UX-BC clause 3).
   */
  onInterceptTap?: () => void
  className?: string
}

/**
 * CTAButton — K6 icon + variant mapper. Always renders a `<button>` (not an
 * anchor). Resubmission relabel (K5) is handled via `cta.label_key` — backend
 * swaps the key to `creator_home.cta.submit_again` when the collab is in a
 * resubmission-eligible state.
 */
export function CTAButton({
  cta,
  missionTitle,
  offline = false,
  onOfflineTap,
  onSubmitTap,
  onInterceptTap,
  className,
}: CTAButtonProps) {
  const { t } = useTranslation()
  const spec = CTA_SPECS[cta.kind]
  // Fallback chain: backend-provided label_key → spec default key. The label
  // comes from the dictionary, not the wire — keeps every string translatable.
  const label = t(cta.label_key || spec.labelFallbackKey)
  // K5 resubmission relabel also swaps the aria key by convention:
  // `creator_home.cta.<name>` → `creator_home.cta.aria.<name>`. Keeps SR
  // output in sync with the visible label without a second backend field.
  const ariaLabel = t(ariaKeyFor(cta.label_key, spec.ariaKey), {
    title: missionTitle,
  })
  const Icon = spec.icon

  // OKS-34/32 — disabled affordances (no href, no handler):
  //   - `awaiting_review`     (Open posted, Drop posted)
  //   - `awaiting_brand`      (Drop applied)
  //   - `to_ship_progress`    (Drop to_ship)
  //   - `to_ship_address_pending` (Drop/Promotional to_ship, address pending)
  //   - `shipped_progress`    (legacy payloads only — OKS-997 removed the
  //                            last Ruby emitter; kept for replayed responses)
  // Render the same Button shape but `disabled` + no click handler so
  // the muted visual + a11y semantics are correct.
  const DISABLED_KINDS: CollabCtaKind[] = [
    // OKS-988 — capacity refusal is a statement, not an action.
    "capacity_refused",
    "awaiting_review",
    "awaiting_brand",
    "to_ship_progress",
    // OKS-1001 — the honest-copy sibling of `to_ship_progress`. Listed here as
    // well as carrying a null href: the href rule would disable it anyway, but
    // a kind missing from this list still LOOKS correct on screen, so the
    // omission would never surface — and this array is what documents intent.
    "to_ship_address_pending",
    // OKS-1040 — declarative, like its address sibling (PRD OQ12).
    "to_ship_product_pending",
    "shipped_progress",
    // OKS-483 — interim "Waiting on {brand}" affordance; no creator action.
    "reopen_requested",
  ]
  // OKS-445 — the `submit` picker-entry CTA carries a nil href by design
  // (it opens a Dialog client-side, it is not a navigation/POST). It must
  // stay actionable, so it is explicitly exempt from the `href === null`
  // disabled rule.
  const isPicker = cta.kind === "submit"
  const disabled =
    !isPicker && (DISABLED_KINDS.includes(cta.kind) || cta.href === null)

  function handleClick() {
    if (disabled) {
      return
    }
    if (offline) {
      onOfflineTap?.()
      return
    }
    // OKS-445 — the `submit` picker-entry CTA opens the OKS-35 TikTok post
    // picker (owned by the sibling SubmissionsPanel) via the lifted state.
    // It is NOT a transition POST.
    if (isPicker) {
      onSubmitTap?.()
      return
    }
    // OKS-990 — on a multi-product drop the primary CTA OPENS THE PICKER
    // instead of POSTing: the creator chooses before the grant, not after.
    //
    // ⚠️ Placed ABOVE the POST branch and returning, never wrapping it. UX-BC
    // clause 3 is "one `router.post`, no intermediate open" for a
    // single-product drop, and its counterpart here is "an open, and NO post" —
    // a dialog that opened AND submitted would satisfy a screenshot and a
    // DOM-absence assertion while breaking both.
    //
    // ⚠️ The caller decides whether this applies. It is present only when the
    // server sent an `offer`, which it does only when R2 holds — so the
    // single-product path never reaches this branch and stays byte-identical.
    if (onInterceptTap) {
      onInterceptTap()
      return
    }
    // OKS-34 — POST CTAs (`accept`, `apply`) use `router.post` so the Inertia
    // transition fires the controller. The `event` field is mirrored into the
    // form body for the controller's strong params.
    if (cta.method === "post" && cta.href) {
      router.post(cta.href, { event: cta.event }, { preserveScroll: true })
      return
    }
    // Default branch — legacy stub `router.visit("#")` (no-op) or any
    // future GET-based CTA. Skip when href is null (typed away above
    // by `disabled` guard but TS still wants a narrowing).
    if (cta.href) {
      router.visit(cta.href)
    }
  }

  return (
    <Button
      type="button"
      variant={spec.variant}
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={`collab-cta-${cta.kind}`}
      data-cta-kind={cta.kind}
      // 44px tap target per UX Spec A11y — `h-11` on shadcn Button.
      className={cn(
        "h-11 w-full rounded-full text-sm font-semibold",
        // OKS-562 §08 — mission-accept confirm nudge on the forward-action CTAs.
        NUDGE_KINDS.includes(cta.kind) && MOTION_BEATS["mission-accept"],
        className,
      )}
    >
      <Icon aria-hidden="true" />
      {label}
    </Button>
  )
}
